import { Box } from '@mui/material';
import { SxProps } from '@mui/material/styles';
import { FC, ReactElement, cloneElement, useEffect, useRef, useState } from 'react';
import { TIMELINE_AXIS_WIDTH } from './styles';
import { TimelinePositioning } from './types';

const minWidthTimeline = 600;

const containerSx: SxProps = {
    minWidth: minWidthTimeline,
    position: 'relative',
};

const axisSx: SxProps = {
    borderColor: 'grey.100',
    borderTop: `${TIMELINE_AXIS_WIDTH}px dashed`,
    marginBottom: 5,
    position: 'relative',
    width: '100%',
    zIndex: 1,
};

export interface TimelineEntry {
    content: ReactElement;
    id: string;
    timestamp: number;
}

interface ElementWithAbscissa extends Omit<TimelineEntry, 'timestamp'> {
    xPosition: number;
}

interface ElementWithCoordinates extends ElementWithAbscissa {
    yPosition: number;
    level: number;
}

interface ComputeAbscissaPositionProps {
    containerWidth: number;
    timelineEntries: TimelineEntry[];
    elementWidth: number;
    min: number;
    range: number;
}

const computeAbscissaPosition = ({
    containerWidth,
    timelineEntries,
    elementWidth,
    min,
    range,
}: ComputeAbscissaPositionProps): ElementWithAbscissa[] =>
    timelineEntries.map(({ content, id, timestamp }) => ({
        content,
        id,
        xPosition: range
            ? ((containerWidth - elementWidth) * (timestamp - min)) / range + elementWidth / 2
            : containerWidth / 2,
    }));

interface ComputeOrdinatePositionProps {
    elementsWithAbscissa: ElementWithAbscissa[];
    elementWidth: number;
}

const computeOrdinatePosition = ({
    elementsWithAbscissa,
    elementWidth,
}: ComputeOrdinatePositionProps): ElementWithAbscissa[][] => {
    if (!elementsWithAbscissa.length) {
        return [];
    }
    elementsWithAbscissa.sort(({ xPosition: x1 }, { xPosition: x2 }) => x1 - x2);

    const elementsWithCoordinates: ElementWithAbscissa[][] = [[elementsWithAbscissa[0]]];
    for (let i = 1; i < elementsWithAbscissa.length; i++) {
        let level = 0;
        while (
            elementsWithCoordinates[level] &&
            elementsWithCoordinates[level][elementsWithCoordinates[level].length - 1].xPosition +
                elementWidth >
                elementsWithAbscissa[i].xPosition
        ) {
            level++;
        }
        if (elementsWithCoordinates[level]) {
            elementsWithCoordinates[level].push(elementsWithAbscissa[i]);
        } else {
            elementsWithCoordinates.push([elementsWithAbscissa[i]]);
        }
    }

    return elementsWithCoordinates;
};

interface TimelineProps {
    timelineEntries: TimelineEntry[];
    elementHeight?: number;
    elementWidth?: number;
    reRenderDependency?: unknown;
}

const Timeline: FC<TimelineProps> = ({
    timelineEntries,
    elementHeight = 75,
    elementWidth = 160,
    reRenderDependency,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(800);

    const defineWidthContainer = () => {
        if (containerRef.current) {
            const { width } = containerRef.current.getBoundingClientRect();
            setContainerWidth(Math.max(width, minWidthTimeline));
        }
    };

    useEffect(() => {
        const handleResize = () => {
            defineWidthContainer();
        };
        handleResize();

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        defineWidthContainer();
    }, [reRenderDependency]);

    const [firstTimestamp, lastTimestamp] = timelineEntries.reduce(
        ([first, last], { timestamp }, index) => {
            if (!index) {
                return [timestamp, timestamp];
            }

            return [Math.min(first, timestamp), Math.max(last, timestamp)];
        },
        [0, 0],
    );
    const timestampRange = lastTimestamp - firstTimestamp;

    const elementsWithAbscissa = computeAbscissaPosition({
        containerWidth,
        elementWidth,
        min: firstTimestamp,
        range: timestampRange,
        timelineEntries,
    });

    const positionedElements = computeOrdinatePosition({
        elementWidth,
        elementsWithAbscissa,
    });

    if (!positionedElements.length) {
        return null;
    }

    const fullHeight = positionedElements.length * elementHeight;
    const elementsWithCoordinates: ElementWithCoordinates[] = positionedElements.flatMap(
        (levelElements, level) =>
            levelElements.map((element) => ({
                ...element,
                level,
                yPosition: fullHeight - elementHeight * (level + 1),
            })),
    );

    return (
        <Box
            ref={containerRef}
            data-test-id="timeline"
            sx={{ my: 8, overflowY: { md: 'visible', xs: 'hidden' } }}
        >
            <Box sx={[containerSx, { height: fullHeight }]}>
                {elementsWithCoordinates.map(({ content, id, level, xPosition, yPosition }) => {
                    // Add position props to content element
                    const positioning: TimelinePositioning = {
                        elementHeight,
                        fullHeight,
                        level,
                        xPos: Math.round(xPosition),
                        yPos: Math.round(yPosition),
                    };

                    return cloneElement(content, {
                        ...content.props,
                        key: id,
                        positioning,
                    });
                })}
            </Box>
            <Box sx={axisSx} />
        </Box>
    );
};

export default Timeline;
